import MorphingLoader from '@app/src/components/MorphingLoader'
import type { AiJob, CompletedJob } from '@app/src/context/AiJobsContext'
import { useAiJobs } from '@app/src/context/AiJobsContext'
import { ViewTypeName } from '@app/src/pages/ResourceCollection/Collections/ManageRequests/ManageRequestsScene'
import { providersByIdLink } from '@app/src/pages/ResourceCollection/Collections/ProvidersMrt/providerTableLink'
import { ProviderTabs } from '@app/src/pages/ResourceCollection/Collections/Providers/ProvidersScene'
import paths from '@app/src/wf-constants/paths'
import { CheckCircleOutlineOutlined } from '@mui/icons-material'
import {
  alpha,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  ClickAwayListener,
  Collapse,
  Divider,
  Fade,
  keyframes,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  styled,
  Typography,
  useTheme,
} from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { generatePath, useHistory } from 'react-router-dom'

const pulseAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`

const checkmarkAnimation = keyframes`
  0% { transform: scale(0) rotate(-45deg); opacity: 0; }
  50% { transform: scale(1.2) rotate(0deg); opacity: 1; }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
`

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(10px); }
  to { opacity: 1; transform: translateX(0); }
`

const slideOut = keyframes`
  from { opacity: 1; transform: translateX(0); max-height: 100px; }
  to { opacity: 0; transform: translateX(-10px); max-height: 0; }
`

const AnimatedCheckIcon = styled(CheckCircleOutlineOutlined)`
  animation: ${checkmarkAnimation} 0.4s ease-out forwards;
`

const AnimatedListItem = styled(ListItem)<{ $isExiting?: boolean }>`
  animation: ${props => (props.$isExiting ? slideOut : slideIn)} 0.3s ease-out forwards;
  overflow: hidden;
`

/** Duration for fade/transition animations in milliseconds */
const ANIMATION_DURATION_MS = 300

interface AiJobItemProps {
  job: AiJob | CompletedJob
  isCompleted?: boolean
  onViewClick?: () => void
  isExiting?: boolean
}

const AiJobItem: React.FC<AiJobItemProps> = ({ job, isCompleted = false, onViewClick, isExiting = false }) => {
  const { formatMessage } = useIntl()
  const { palette } = useTheme()

  const getJobTitle = () => {
    switch (job.type) {
      case 'screening':
        return formatMessage({ id: 'navbar.aiJobs.screening.title' })
      case 'enrichment':
        return formatMessage({ id: 'navbar.aiJobs.enrichment.title' })
      default:
        return ''
    }
  }

  const getJobDescription = () => {
    if (isCompleted) {
      return formatMessage({ id: 'navbar.aiJobs.completed' })
    }
    const inProgressCount = 'inProgressCount' in job ? job.inProgressCount : 0
    switch (job.type) {
      case 'screening':
        return formatMessage({ id: 'navbar.aiJobs.screening.description' }, { count: inProgressCount })
      case 'enrichment':
        return formatMessage({ id: 'navbar.aiJobs.enrichment.description' }, { count: inProgressCount })
      default:
        return ''
    }
  }

  return (
    <AnimatedListItem
      $isExiting={isExiting}
      sx={{
        px: 2,
        py: 1.5,
        transition: 'background-color 0.2s ease',
        '&:hover': {
          backgroundColor: alpha(palette.action.hover, 0.04),
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 40 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: 1,
            backgroundColor: isCompleted ? alpha(palette.success.main, 0.1) : alpha(palette.communication.main, 0.1),
            transition: 'background-color 0.3s ease',
          }}
        >
          {isCompleted ? (
            <AnimatedCheckIcon sx={{ color: palette.secondary.light, fontSize: 20 }} />
          ) : (
            <CircularProgress size={20} sx={{ color: palette.communication.dark }} />
          )}
        </Box>
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography variant="subtitle2" fontWeight={600}>
            {getJobTitle()}
          </Typography>
        }
        secondary={
          <Typography variant="body2" color="text.secondary">
            {getJobDescription()}
          </Typography>
        }
        sx={{ flex: 1 }}
      />
      {isCompleted && onViewClick && (
        <Fade in timeout={ANIMATION_DURATION_MS}>
          <Button
            size="small"
            onClick={e => {
              e.stopPropagation()
              onViewClick()
            }}
            sx={{
              ml: 1,
              textTransform: 'none',
              minWidth: 'auto',
            }}
          >
            {formatMessage({ id: 'schemas.notification.view' })}
          </Button>
        </Fade>
      )}
    </AnimatedListItem>
  )
}

const AUTO_DISMISS_TIMEOUT = 60000

const AiJobsStatusIndicator: React.FC = () => {
  const { palette } = useTheme()
  const history = useHistory()
  const { formatMessage } = useIntl()
  const { jobs, completedJobs, markJobAsViewed, clearCompletedJobs } = useAiJobs()
  const [open, setOpen] = useState(false)
  const [exitingJobId, setExitingJobId] = useState<string | null>(null)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const autoDismissTimerRef = useRef<NodeJS.Timeout | null>(null)
  const [showIndicator, setShowIndicator] = useState(false)

  const hasRunningJobs = jobs.length > 0
  const hasCompletedJobs = completedJobs.length > 0
  const hasAnyContent = hasRunningJobs || hasCompletedJobs

  useEffect(() => {
    if (hasAnyContent) {
      setShowIndicator(true)
    }
  }, [hasAnyContent])

  useEffect(() => {
    if (autoDismissTimerRef.current) {
      clearTimeout(autoDismissTimerRef.current)
      autoDismissTimerRef.current = null
    }

    if (hasRunningJobs || !hasCompletedJobs) {
      return
    }

    autoDismissTimerRef.current = setTimeout(() => {
      clearCompletedJobs()
      setShowIndicator(false)
      setOpen(false)
    }, AUTO_DISMISS_TIMEOUT)

    return () => {
      if (autoDismissTimerRef.current) {
        clearTimeout(autoDismissTimerRef.current)
        autoDismissTimerRef.current = null
      }
    }
  }, [hasRunningJobs, hasCompletedJobs, clearCompletedJobs])

  useEffect(() => {
    if (!hasAnyContent && showIndicator) {
      const timeout = setTimeout(() => {
        setShowIndicator(false)
      }, ANIMATION_DURATION_MS)
      return () => clearTimeout(timeout)
    }
  }, [hasAnyContent, showIndicator])

  const handleClick = useCallback(() => {
    setOpen(prev => !prev)
  }, [])

  const handleViewClick = useCallback(
    (job: CompletedJob) => {
      setExitingJobId(job.id)

      setTimeout(() => {
        if (job.type === 'enrichment') {
          history.push(
            providersByIdLink(
              ProviderTabs.All,
              (job.enrichedProviderIds ?? []).map(id => ({ id })),
            ),
          )
        } else if (job.type === 'screening') {
          const path = generatePath(paths.manageRequest, { view: ViewTypeName.ScreenedRequests })
          history.push(path)
        }

        markJobAsViewed(job.id)
        setExitingJobId(null)

        if (completedJobs.length <= 1 && !hasRunningJobs) {
          setOpen(false)
        }
      }, ANIMATION_DURATION_MS)
    },
    [history, markJobAsViewed, completedJobs.length, hasRunningJobs],
  )

  const handleClickAway = useCallback(() => {
    setOpen(false)
  }, [])

  const allCompleted = !hasRunningJobs && hasCompletedJobs
  const isInProgress = hasRunningJobs

  const displayedJobs = useMemo(() => {
    const running = jobs.map(job => ({ ...job, isCompleted: false }))
    const completed = completedJobs.map(job => ({ ...job, isCompleted: true }))
    return [...running, ...completed]
  }, [jobs, completedJobs])

  if (!showIndicator) {
    return null
  }

  const backgroundColor = allCompleted
    ? open
      ? palette.success.main
      : palette.success.light
    : open
      ? palette.communication.main
      : palette.communication.light

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ display: 'inline-flex' }}>
        <Fade in={showIndicator} timeout={400}>
          <ButtonBase
            ref={anchorRef}
            onClick={handleClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 0.5,
              transition: 'all 0.3s ease-in-out',
              animation: isInProgress ? `${pulseAnimation} 2s ease-in-out infinite` : 'none',
              backgroundColor: open ? backgroundColor : 'transparent',
              '&:hover': {
                backgroundColor: backgroundColor,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                backgroundColor: backgroundColor,
                p: 1,
              }}
            >
              {allCompleted ? (
                <AnimatedCheckIcon
                  sx={{
                    width: 24,
                    height: 24,
                    color: palette.secondary.light,
                    m: 0.5,
                  }}
                />
              ) : (
                <MorphingLoader size={32} color={palette.communication.dark} transitionDuration={400} />
              )}
            </Box>
          </ButtonBase>
        </Fade>

        <Popper
          open={open && showIndicator}
          anchorEl={anchorRef.current}
          placement="bottom-end"
          transition
          sx={{ zIndex: 1300 }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={200}>
              <Paper
                elevation={8}
                sx={{
                  mt: 1,
                  minWidth: 340,
                  maxWidth: 420,
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <Collapse in={displayedJobs.length > 0}>
                  <List disablePadding sx={{ maxHeight: 320, overflowY: 'auto' }}>
                    {displayedJobs.map((job, index) => {
                      const isCompleted = 'completedAt' in job
                      const completedJob = isCompleted ? (job as CompletedJob) : undefined

                      return (
                        <React.Fragment key={job.id}>
                          <AiJobItem
                            job={job}
                            isCompleted={isCompleted}
                            onViewClick={completedJob ? () => handleViewClick(completedJob) : undefined}
                            isExiting={exitingJobId === job.id}
                          />
                          {index < displayedJobs.length - 1 && <Divider />}
                        </React.Fragment>
                      )
                    })}
                  </List>
                </Collapse>

                <Collapse in={displayedJobs.length === 0}>
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {formatMessage({ id: 'navbar.aiJobs.noJobs' })}
                    </Typography>
                  </Box>
                </Collapse>
              </Paper>
            </Fade>
          )}
        </Popper>
      </Box>
    </ClickAwayListener>
  )
}

export default AiJobsStatusIndicator
